import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Eyebrow, Reveal, Section } from "@/components/Primitives";
import { BookCallButton } from "@/context/BookCall";
import { PRINCIPLES, STAGES, ROI, CLIENTS, clientName } from "@/lib/data";
import { cn } from "@/lib/utils";

export function Principles() {
    return (
        <Section>
            <Reveal>
                <Eyebrow>How we work</Eyebrow>
                <h2 className="mt-4 font-display text-4xl md:text-5xl font-bold tracking-tight max-w-2xl">
                    Three principles behind every engagement
                </h2>
            </Reveal>
            <div className="mt-14 grid gap-4 md:grid-cols-3">
                {PRINCIPLES.map((p, i) => (
                    <Reveal key={p.n} delay={i * 0.06}>
                        <div className="group h-full bg-white border border-pa-line p-7 cut-tr-lg hover:border-pa-blue-600 transition-colors">
                            <span className="font-mono text-sm text-pa-blue-600">
                                {p.n}
                            </span>
                            <h3 className="mt-3 font-display text-xl font-bold leading-snug">
                                {p.title}
                            </h3>
                            <p className="mt-3 text-pa-ink-500 leading-relaxed">
                                {p.body}
                            </p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </Section>
    );
}

export function EngagementPath({ dark = false }) {
    const reduce = useReducedMotion();
    return (
        <Section dark={dark}>
            <Reveal>
                <Eyebrow light={dark}>Engagement path</Eyebrow>
                <h2 className="mt-4 font-display text-4xl md:text-5xl font-bold tracking-tight max-w-2xl">
                    A short, fixed path from problem to running system
                </h2>
            </Reveal>

            <div className="mt-14 relative">
                <div
                    className={cn(
                        "absolute left-0 top-[26px] h-px w-full",
                        dark ? "bg-white/15" : "bg-pa-line",
                    )}
                    aria-hidden="true"
                />
                <motion.div
                    className="absolute left-0 top-[26px] h-px bg-pa-blue-600 origin-left"
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: reduce ? 0 : 1.1, ease: "easeInOut" }}
                    style={{ width: "100%" }}
                    aria-hidden="true"
                />
                <div className="grid gap-8 md:grid-cols-3 relative">
                    {STAGES.map((s, i) => (
                        <Reveal key={s.n} delay={i * 0.12}>
                            <div>
                                <div
                                    className={cn(
                                        "h-[52px] w-[52px] flex items-center justify-center font-mono font-medium cut-tr-sm",
                                        "bg-pa-blue-600 text-white",
                                    )}
                                >
                                    {s.n}
                                </div>
                                <div
                                    className={cn(
                                        "mt-5 font-mono text-xs uppercase tracking-widest",
                                        dark
                                            ? "text-pa-blue-050/70"
                                            : "text-pa-ink-500",
                                    )}
                                >
                                    {s.duration}
                                </div>
                                <h3 className="mt-1 font-display text-2xl font-bold">
                                    {s.title}
                                </h3>
                                <p
                                    className={cn(
                                        "mt-2 leading-relaxed",
                                        dark
                                            ? "text-pa-blue-050/70"
                                            : "text-pa-ink-500",
                                    )}
                                >
                                    {s.body}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </Section>
    );
}

export function RoiSection() {
    return (
        <Section className="bg-pa-blue-050">
            <Reveal>
                <Eyebrow>Where the cost actually sits</Eyebrow>
                <h2 className="mt-4 font-display text-4xl md:text-5xl font-bold tracking-tight max-w-3xl">
                    We show you the mechanism. You do the arithmetic against your
                    own numbers.
                </h2>
            </Reveal>

            <div className="mt-12 bg-white border border-pa-line cut-tr-lg overflow-hidden">
                <div className="hidden md:grid grid-cols-3 font-mono text-xs uppercase tracking-widest text-pa-ink-500 border-b border-pa-line">
                    <div className="p-5">Today</div>
                    <div className="p-5 border-x border-pa-line">
                        What the agent absorbs
                    </div>
                    <div className="p-5">What changes structurally</div>
                </div>
                {ROI.map((r, i) => (
                    <Reveal
                        key={i}
                        delay={i * 0.04}
                        className={cn(
                            "grid md:grid-cols-3",
                            i < ROI.length - 1 && "border-b border-pa-line",
                        )}
                    >
                        <div className="p-5">
                            <span className="md:hidden font-mono text-[11px] uppercase tracking-widest text-pa-ink-500 block mb-1">
                                Today
                            </span>
                            <p className="text-pa-ink-900">{r.today}</p>
                        </div>
                        <div className="p-5 md:border-x border-pa-line bg-pa-blue-050/40">
                            <span className="md:hidden font-mono text-[11px] uppercase tracking-widest text-pa-ink-500 block mb-1">
                                Agent absorbs
                            </span>
                            <p className="text-pa-ink-900">{r.absorbs}</p>
                        </div>
                        <div className="p-5">
                            <span className="md:hidden font-mono text-[11px] uppercase tracking-widest text-pa-ink-500 block mb-1">
                                Changes
                            </span>
                            <p className="text-pa-blue-700 font-medium">
                                {r.changes}
                            </p>
                        </div>
                    </Reveal>
                ))}
            </div>

            <Reveal>
                <blockquote className="mt-12 border-l-2 border-pa-blue-600 pl-6 max-w-3xl">
                    <p className="font-display text-2xl md:text-3xl font-bold leading-snug text-pa-ink-900">
                        The size of the return depends on your volumes, your
                        handling time, and your fully loaded cost per
                        interaction. We baseline those numbers with you in the
                        first two weeks of discovery, before you commit to a
                        build.
                    </p>
                    <footer className="mt-4 font-mono text-xs uppercase tracking-widest text-pa-ink-500">
                        We measure before we promise
                    </footer>
                </blockquote>
            </Reveal>
        </Section>
    );
}

export function CaseCard({ c, index = 0 }) {
    return (
        <Reveal delay={index * 0.06}>
            <Link
                to={`/work/${c.slug}`}
                data-testid={`case-card-${c.slug}`}
                className="group block h-full bg-white border border-pa-line p-7 cut-tr-lg hover:border-pa-blue-600 transition-colors"
            >
                <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-widest text-pa-ink-500">
                    <span className="text-pa-blue-700">{c.sector}</span>
                    <span>·</span>
                    <span>{c.region}</span>
                </div>
                <h3 className="mt-4 font-display text-2xl font-bold leading-tight">
                    {c.project ? `${c.project} · ${clientName(c)}` : clientName(c)}
                </h3>
                <p className="mt-3 text-pa-ink-500 leading-relaxed">
                    {c.summary}
                </p>
                <span className="mt-5 inline-flex items-center gap-1.5 font-mono text-sm text-pa-blue-700">
                    Read case
                    <span className="transition-transform group-hover:translate-x-1">
                        →
                    </span>
                </span>
            </Link>
        </Reveal>
    );
}

export function CtaBanner() {
    return (
        <Section dark>
            <div className="grid gap-8 md:grid-cols-[1.4fr_1fr] md:items-end">
                <Reveal>
                    <Eyebrow light>Next step</Eyebrow>
                    <h2 className="mt-4 font-display text-4xl md:text-6xl font-bold tracking-tight leading-[1.05]">
                        Start with a 1–2 week discovery. No build commitment.
                    </h2>
                    <p className="mt-5 text-lg text-pa-blue-050/70 max-w-xl">
                        We map how your team works today and define the
                        automation opportunity before anyone writes code.
                    </p>
                </Reveal>
                <Reveal delay={0.1} className="md:justify-self-end">
                    <BookCallButton variant="onDark" testId="cta-banner-book" />
                </Reveal>
            </div>
        </Section>
    );
}

export function featuredClients() {
    return CLIENTS.filter((c) => c.featured);
}
